import { useCallback, useMemo, useState } from 'react'

import {
  logError,
  mapApiResponseToFieldValidationErrors
} from '@sequencehq/utils'

import { useNotifications } from '@sequencehq/core-components'
import { isSequenceApiError, isSequenceApiErrorResponse } from '@sequencehq/api'
import { CustomerContact } from '../types'
import { CustomerContactFields } from '../view/forms/ContactModalForm/useContactModalForm'
import {
  CustomerContactPorts,
  CustomerContactPostBody,
  CustomerContactPutBody
} from '../communication/external/v1/ports/customerContacts.ports'
import {
  INITIAL_DATA,
  INITIAL_FORM_DATA
} from './CustomerContacts.domain.constants'
import { updateContactsState } from '../utils/updateContactsState'
import {
  assignContactPurpose,
  unassignContactPurpose
} from '../utils/contactPurposes'
import { useCustomerContext } from 'Customer/hooks/useCustomerContext'

export type CustomerContactsDomainInterfaceData = {
  customerId: string
  contacts: CustomerContact[]
}

export type CustomerContactsDomainInterface = {
  queries: {
    data: CustomerContactsDomainInterfaceData
    formData: CustomerContactFields
  }
  mutators: {
    external: {
      in: {
        core: () => Promise<void>
      }
      out: {
        createContact: (body: CustomerContactPostBody) => Promise<void>
        updateContact: ({
          contactId,
          body
        }: {
          contactId: string
          body: CustomerContactPutBody
        }) => Promise<void>
        markAsPrimaryBillingContact: (contactId: string) => Promise<void>
        markAsShippingContact: (contactId: string) => Promise<void>
        unmarkAsShippingContact: (contactId: string) => Promise<void>
        archiveContact: (contactId: string) => Promise<void>
      }
    }
    internal: {
      updateForm: (newFields: CustomerContactFields) => void
    }
  }
}

type UseCustomerContactsDomain = (props: {
  ports: CustomerContactPorts
}) => CustomerContactsDomainInterface

const hasFieldValidationErrors = async (error: unknown): Promise<boolean> => {
  if (
    !isSequenceApiError(error) ||
    !isSequenceApiErrorResponse(error.response)
  ) {
    return false
  }

  const fieldValidationErrors = await mapApiResponseToFieldValidationErrors(
    error.response
  )

  return Object.values(fieldValidationErrors).some(errors => errors.length > 0)
}

export const useCustomerContactsDomain: UseCustomerContactsDomain = props => {
  const { displayNotification } = useNotifications()

  const { customer } = useCustomerContext()

  const [data, setData] =
    useState<CustomerContactsDomainInterfaceData>(INITIAL_DATA)

  const [formData, setFormData] =
    useState<CustomerContactFields>(INITIAL_FORM_DATA)

  const queries = useMemo(() => ({ data, formData }), [data, formData])

  /**
   * Load contacts
   */
  const loadData = props.ports.load
  const load = useCallback(async () => {
    const loadedData = await loadData()

    setData(loadedData)
  }, [loadData])

  /**
   * Create contacts
   */
  const portCreateContact = props.ports.createContact
  const createContact = useCallback(
    async (body: CustomerContactPostBody) => {
      try {
        const newContact = await portCreateContact(body)

        setData(prev => ({
          ...prev,
          contacts: [...prev.contacts, newContact]
        }))

        displayNotification('Contact created', {
          type: 'success',
          description: `${
            newContact.name ?? newContact.email
          } was just added to ${customer.legalName}`,
          isClosable: true
        })
      } catch (e) {
        logError(e)

        if (await hasFieldValidationErrors(e)) {
          throw e
        }

        displayNotification('Unable to create new contact', {
          type: 'error'
        })
      }
    },
    [displayNotification, portCreateContact]
  )

  /**
   * Update contacts
   */
  const portUpdateContact = props.ports.updateContact
  const updateContact = useCallback(
    async ({
      contactId,
      body
    }: {
      contactId: string
      body: CustomerContactPutBody
    }) => {
      try {
        const updatedContact = await portUpdateContact({ contactId, body })

        setData(prev => ({
          ...prev,
          contacts: updateContactsState({
            contactId,
            previousContacts: prev.contacts,
            updatedContact
          })
        }))

        displayNotification('Contact updated', {
          type: 'success',
          description: `${
            updatedContact.name ?? updatedContact.email
          } is now updated`,
          isClosable: true
        })
      } catch (e) {
        logError(e)

        if (await hasFieldValidationErrors(e)) {
          throw e
        }

        displayNotification('Unable to update contact', {
          type: 'error'
        })
      }
    },
    [displayNotification, portUpdateContact]
  )

  /**
   * Primary billing and shipping roles
   */
  const portUpdateContacts = props.ports.updateContacts
  const updateContactRoles = useCallback(
    async ({
      contacts,
      successMessage,
      errorMessage
    }: {
      contacts: CustomerContact[]
      successMessage: string
      errorMessage: string
    }) => {
      try {
        await portUpdateContacts(contacts)

        setData(prev => ({ ...prev, contacts }))

        displayNotification(successMessage, { type: 'success' })
      } catch (e) {
        logError(e)

        displayNotification(errorMessage, { type: 'error' })
      }
    },
    [displayNotification, portUpdateContacts]
  )

  const markAsPrimaryBillingContact = useCallback(
    (contactId: string) =>
      updateContactRoles({
        contacts: assignContactPurpose(data.contacts, contactId, 'BILLING'),
        successMessage: 'Primary billing contact updated',
        errorMessage: 'Unable to update primary billing contact'
      }),
    [data.contacts, updateContactRoles]
  )

  const markAsShippingContact = useCallback(
    (contactId: string) =>
      updateContactRoles({
        contacts: assignContactPurpose(data.contacts, contactId, 'SHIPPING'),
        successMessage: 'Shipping contact updated',
        errorMessage: 'Unable to update shipping contact'
      }),
    [data.contacts, updateContactRoles]
  )

  const unmarkAsShippingContact = useCallback(
    (contactId: string) =>
      updateContactRoles({
        contacts: unassignContactPurpose(data.contacts, contactId, 'SHIPPING'),
        successMessage: 'Shipping contact updated',
        errorMessage: 'Unable to update shipping contact'
      }),
    [data.contacts, updateContactRoles]
  )

  /**
   * Archive contacts
   */
  const portArchiveContact = props.ports.archiveContact
  const archiveContact = useCallback(
    async (contactId: string) => {
      try {
        const selectedContact = data.contacts.find(
          contact => contact.id === contactId
        )

        if (!selectedContact) {
          throw new Error('Unable to find contact')
        }

        await portArchiveContact(contactId)

        setData(prev => ({
          ...prev,
          contacts: prev.contacts.filter(contact => contact.id !== contactId)
        }))

        displayNotification('Contact removed', {
          type: 'success',
          description: `${
            selectedContact.name ?? selectedContact.email
          } has been removed from ${customer.legalName}`,
          isClosable: true
        })
      } catch (e) {
        logError(e)

        displayNotification('Unable to archive contact', {
          type: 'error'
        })
      }
    },
    [data.contacts, displayNotification, portArchiveContact]
  )

  /**
   * Forms
   */
  const updateForm = useCallback((newFields: CustomerContactFields) => {
    setFormData(newFields)
  }, [])

  return {
    queries,
    mutators: {
      external: {
        in: { core: load },
        out: {
          createContact,
          updateContact,
          markAsPrimaryBillingContact,
          markAsShippingContact,
          unmarkAsShippingContact,
          archiveContact
        }
      },
      internal: {
        updateForm
      }
    }
  }
}
